import { useCallback, useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { makeStyles, withStyles } from "@material-ui/core/styles";

import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from "@material-ui/core";

import EmailIcon from "@mui/icons-material/Email";
import KeyboardBackspace from "@mui/icons-material/KeyboardBackspace";

import PasswordField from "components/_refactor/fields/password";
import InputText from "components/_refactor/text/input";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import useAlert from "components/Alerts/useAlert";
import MultipleSelect from "components/Input/MultipleSelect";
import componentStyles from "assets/theme/views/auth/login";
import componentStylesButtons from "assets/theme/components/button.js";
import MaskAndValid from "utils/maskAndValid";
import Api from "lib/api/httpRequestHelper";
import UtilsFncs from "utils/UtilsFncs";
import { Link } from "react-router-dom";
import { IsMobile } from "utils/is-mobile";

const useStyles = makeStyles(componentStyles);
const useStylesButtons = makeStyles(componentStylesButtons);
const useExtraStyles = makeStyles({
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  displayBlock: {
    display: "block",
    marginTop: 10,
  },
  errorMessageLink: {
    cursor: "pointer",
  },
  input: {
    "& input[type=number]": {
      "-moz-appearance": "textfield",
    },
    "& input[type=number]::-webkit-outer-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
    "& input[type=number]::-webkit-inner-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
  },
});

const RadioOption = withStyles(() => ({
  label: {
    fontSize: "1rem",
    color: "#FFFFFF",
  },
  root: {
    marginBottom: 0,
  },
}))(FormControlLabel);

const filterRepeated = (options) => {
  return options.filter((option, index) => {
    return (
      options.findIndex((optionToFind) => {
        return optionToFind.Id === option.Id;
      }) === index
    );
  });
};

export default function Register() {
  const classes = {
    ...useStyles(),
    ...useStylesButtons(),
    ...useExtraStyles(),
  };
  const snackbar = useSnackbar();

  const isMobile = IsMobile();

  const [iAm, setIAm] = useState("lojista");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [job, setJob] = useState("");
  const [phone, setPhone] = useState("");
  const [representativeCode, setRepresentativeCode] = useState("");
  const [creating, setCreating] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmationPassword, setConfirmationPassword] = useState("");
  const [passwordIsOk, setPasswordIsOk] = useState(false);
  const [gruposEmpresariais, setGruposEmpresariais] = useState(null);
  const [initialSelected, setInitialSelected] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState(null);
  const [validatingCNPJ, setValidatingCNPJ] = useState(false);
  const recaptchaRef = useRef();
  const { alert, closeAlert, showSuccessAlert, showErrorAlert } = useAlert();

  const isCnpjValid = MaskAndValid.validateCNPJ(cnpj);

  const handleChange = useCallback((event) => {
    if (event.target.name) {
      setIAm(event.target.value);
      setCnpj("");
    }
  }, []);

  const submit = useCallback(async () => {
    setCreating(true);

    if (iAm === "lojista") {
      setRepresentativeCode("");
    }

    const tokenReCaptcha = await recaptchaRef.current.executeAsync();
    recaptchaRef.current.reset();

    await Api.post(
      "create-user",
      "login",
      {
        nome: name,
        tokenReCaptcha: tokenReCaptcha,
        login: email,
        senha: password,
        cnpj: cnpj,
        lojista: iAm === "lojista",
        representante: iAm === "representante",
        cargo: job ? job : "",
        telefone: phone ? phone : "",
        codigoERP: iAm === "representante" ? representativeCode : "",
        idsLojas: selectedGroups
          ? filterRepeated(selectedGroups)
              ?.map((grupo) => grupo.Id)
              .join(",")
          : "",
      },
      true
    )
      .then(({ Result, Success }) => {
        if (Success) {
          showSuccessAlert(
            Result.MsgRetornoFront,
            "Usuário Criado com sucesso",
            UtilsFncs.RedirectToLoginPage,
            UtilsFncs.RedirectToLoginPage
          );
        }
      })
      .catch(() => {
        showErrorAlert(
          <>
            <span className={classes.displayBlock}>
              O e-mail informado não é válido para nenhum lojista. Entre em
              contato com a central de relacionamento com o cliente através do
              site:
            </span>
            <a
              className={classes.errorMessageLink}
              target="_blank"
              rel="noreferrer"
              href="https://www.karstensa.com.br/portal-do-lojista"
            >
              <span className={classes.displayBlock}>
                https://www.karstensa.com.br/portal-do-lojista
              </span>
            </a>
            <span className={classes.displayBlock}>
              ou através dos telefones
            </span>
            <div className={classes.displayBlock}>
              <span>
                Pessoa Física:
                <a href="tel:0800 747 3737"> 0800 747 3737 </a>
              </span>
            </div>

            <div className={classes.displayBlock}>
              <span>
                Pessoa Jurídica:
                <a href="tel:0800 747 3737"> 0800 647 4404</a>
              </span>
            </div>
          </>,
          "Ops!",
          closeAlert,
          closeAlert
        );
      })
      .finally(() => setCreating(false));
  }, [
    closeAlert,
    cnpj,
    email,
    iAm,
    job,
    name,
    password,
    phone,
    selectedGroups,
    showErrorAlert,
    showSuccessAlert,
    representativeCode,
  ]);

  const validateCNPJ = useCallback(
    async (cnpj) => {
      setValidatingCNPJ(true);
      setGruposEmpresariais(null);
      setSelectedGroups(null);

      await Api.get(
        "validate-registration",
        "login",
        "?cnpj=" +
          cnpj +
          "&email=" +
          email +
          "&lojista=" +
          (iAm === "lojista") +
          "&representante=" +
          (iAm === "representante"),
        false
      )
        .then(({ Result }) => {
          if (Result.CnpjValido) {
            const groupList = [];

            // eslint-disable-next-line array-callback-return
            Result.LojasGrupoEmpresarial?.map((group) => {
              if (group.Nome) {
                const cnpjFormatter = group.Cnpj?.replace(
                  /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
                  "$1.$2.$3/$4-$5"
                );
                const newGroup = {
                  Cnpj: group.Cnpj,
                  Id: group.Id,
                  Nome: `${group.Nome} | ${cnpjFormatter}`,
                  Selecionado: group.Selecionado,
                };
                groupList.push(newGroup);
                setGruposEmpresariais(groupList);

                setInitialSelected(
                  groupList.filter((group) => group.Selecionado)
                );
              }
            });
          } else {
            setInitialSelected(null);
            setGruposEmpresariais(null);
          }
        })
        .catch((error) => {
          snackbar.showMessage(error, {
            severity: "error",
          });
        })
        .finally(() => setValidatingCNPJ(false));
    },
    [iAm, snackbar, email]
  );

  const handleCNPJChange = () => {
    if (MaskAndValid.validateCNPJ(cnpj) && MaskAndValid.validateEmail(email)) {
      validateCNPJ(cnpj);
    } else if (!MaskAndValid.validateCNPJ(cnpj)) {
      snackbar.showMessage("CNPJ inválido", {
        severity: "error",
      });
    }
  };

  const handleEmailChange = () => {
    if (MaskAndValid.validateCNPJ(cnpj) && MaskAndValid.validateEmail(email)) {
      validateCNPJ(cnpj);
    }
  };

  useEffect(() => {
    if (email && MaskAndValid.validateEmail(email)) {
      handleEmailChange();
    } else if (email && !MaskAndValid.validateEmail(email)) {
      snackbar.showMessage("E-mail inválido", {
        severity: "error",
      });
    }
  }, [email]);

  useEffect(() => {
    if (cnpj?.length === 18) {
      handleCNPJChange();
    }
  }, [cnpj]);

  return (
    <>
      {alert}

      <Container maxWidth="md">
        <Card classes={{ root: classes.card }}>
          <Link className={classes.back} to={"login"}>
            <KeyboardBackspace /> Voltar
          </Link>

          <CardContent classes={{ root: classes.cardContent }}>
            <Grid Container spacing={2}>
              <Grid item xs={12}>
                <InputText
                  label="Nome Completo"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nome Completo"
                  disabled={creating}
                  required
                  onInput={(e) => {
                    e.target.value = e.target.value.toString().slice(0, 200);
                  }}
                  white
                />
              </Grid>

              <Grid item xs={12}>
                <InputText
                  label="E-mail"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="E-mail"
                  startAdornment={<EmailIcon />}
                  disabled={creating}
                  required
                  onBlur={() => {
                    if (email && !MaskAndValid.validateEmail(email)) {
                      snackbar.showMessage("E-mail inválido", {
                        severity: "error",
                      });
                    }
                  }}
                  onInput={(e) =>
                    (e.target.value = e.target.value.toString().slice(0, 50))
                  }
                  white
                />
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <InputText
                    label="Meu Cargo"
                    type="text"
                    value={job}
                    onChange={(e) => setJob(e.target.value)}
                    placeholder="Meu Cargo"
                    disabled={creating}
                    required
                    onInput={(e) =>
                      (e.target.value = e.target.value.toString().slice(0, 50))
                    }
                    white
                  />
                </Grid>

                <Grid item xs={6}>
                  <InputText
                    label="Telefone"
                    type="text"
                    value={phone}
                    onChange={(e) =>
                      setPhone(MaskAndValid.maskPhone(e.target.value))
                    }
                    placeholder="Telefone"
                    disabled={creating}
                    required
                    onInput={(e) =>
                      (e.target.value = e.target.value.toString().slice(0, 50))
                    }
                    white
                  />
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <FormGroup
                  row
                  style={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    style={{ marginRight: "1.2rem" }}
                    color="textSecondary"
                  >
                    Eu sou:{" "}
                  </Typography>
                  <FormControl component="fieldset" required>
                    <RadioGroup
                      aria-label="eu sou"
                      name="iAm"
                      defaultValue="lojista"
                      value={iAm}
                      onChange={handleChange}
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        marginBottom: 0,
                      }}
                      required
                    >
                      <RadioOption
                        value="representante"
                        control={<Radio size="small" color="primary" />}
                        label="Representante"
                      />
                      <RadioOption
                        value="lojista"
                        control={<Radio size="small" color="primary" />}
                        label="Lojista"
                      />
                    </RadioGroup>
                  </FormControl>
                </FormGroup>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={6} hidden={iAm !== "representante"}>
                  <InputText
                    label="Código ERP"
                    type="number"
                    value={representativeCode}
                    onChange={(e) => setRepresentativeCode(e.target.value)}
                    placeholder="Código ERP"
                    disabled={creating}
                    required
                    onInput={(e) =>
                      (e.target.value = e.target.value.toString().slice(0, 10))
                    }
                    white
                  />
                </Grid>

                <Grid item xs={iAm !== "representante" ? 12 : 6}>
                  <InputText
                    label="CNPJ"
                    type="text"
                    value={cnpj}
                    onChange={(e) =>
                      setCnpj(MaskAndValid.maskCNPJ(e.target.value))
                    }
                    placeholder="CNPJ"
                    disabled={creating}
                    required
                    onBlur={() => {
                      if (cnpj && !MaskAndValid.validateCNPJ(cnpj)) {
                        snackbar.showMessage("CNPJ inválido", {
                          severity: "error",
                        });
                      }
                    }}
                    onInput={(e) => {
                      e.target.value = e.target.value.toString().slice(0, 18);
                    }}
                    white
                  />
                  {validatingCNPJ && (
                    <Box display="flex" alignItems="center" marginTop="1rem">
                      <CircularProgress
                        style={{ height: 18, width: 18, marginRight: "1rem" }}
                      />
                      Validando CNPJ
                    </Box>
                  )}
                </Grid>

                <Grid
                  item
                  xs={12}
                  hidden={
                    !isCnpjValid ||
                    validatingCNPJ ||
                    !gruposEmpresariais ||
                    !initialSelected
                  }
                >
                  <MultipleSelect
                    inputLabel="Ter acesso a lojas"
                    initialSelected={initialSelected}
                    options={gruposEmpresariais}
                    onSelectedChange={setSelectedGroups}
                    variant="filled"
                    required
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <PasswordField
                    label="Senha"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    strengthInfo
                    white
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <PasswordField
                    label="Confirme a senha"
                    value={confirmationPassword}
                    onChange={(e) => setConfirmationPassword(e.target.value)}
                    white
                    confirmPassword={password}
                    setPasswordIsOk={setPasswordIsOk}
                    required
                  />
                </Grid>
              </Grid>
            </Grid>

            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.REACT_APP_RECAPTCHA_PROD_REACT}
              size="invisible"
            />

            <Box textAlign="center">
              <Button
                variant="contained"
                style={isMobile ? { width: "100%" } : { width: "15vw" }}
                classes={{ root: classes.buttonContainedInfo }}
                disabled={
                  creating ||
                  !isCnpjValid ||
                  validatingCNPJ ||
                  !MaskAndValid.validateEmail(email) ||
                  MaskAndValid.isNullOrEmpty(name) ||
                  MaskAndValid.isNullOrEmpty(job) ||
                  (iAm === "representante" &&
                    MaskAndValid.isNullOrEmpty(representativeCode)) ||
                  !MaskAndValid.validatePhone(phone) ||
                  !passwordIsOk ||
                  !(
                    password?.length &&
                    confirmationPassword?.length &&
                    password === confirmationPassword
                  ) ||
                  !(selectedGroups && selectedGroups.length > 0)
                }
                onClick={submit}
              >
                {creating ? "Criando conta..." : "Criar conta"}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </>
  );
}
